import moment from 'moment/moment'

import { PIXELS_PER_MINUTE } from '../utils/calendar'
import {
    Color,
    lookupBookableColor,
    lookupBookableDefaultColor,
    lookupBookableStatusColor,
    lookupEmployeeColor,
} from '../utils/calendarColors'
import {
    DATE_KEY_FORMAT,
    TIME_FORMAT,
    getDurationInMinutes,
    getMinutesFromStartOfDay,
    getMinutesToEndOfDay,
    isPast,
} from '../utils/dates'
import { Block } from './Block'
import { Booking } from './Booking'
import { Item } from './Item'
import { Lesson } from './Lesson'
import { Order } from './Order'
import { PSEvent } from './PSEvent'
import { Payee } from './Payee'
import { Subaccount } from './Subaccount'
import { Task } from './Task'
import { User } from './User'

export enum ScheduleState {
    Chargeable,
    Payable,
    Editable,
    Cancelable,
    Confirmable,
    Completable,
    Complete,
}

export interface Bookable {
    iBookable: true
    owner?: User
    person?: User
    subaccount?: Subaccount
    date?: moment.Moment
    id?: string
    endDate?: moment.Moment
    createdDate?: moment.Moment
    confirmedDate?: moment.Moment
    cancelledDate?: moment.Moment
    memo?: string
    duration?: number
    location?: string
    title?: string
    get shortTitle(): string | undefined
    color?: string
    employee?: User
    get allowJoin(): boolean
    get allowCheckin(): boolean
    get allowNotify(): boolean
    get hasSchedule(): boolean
    get isRecurring(): boolean
    get ownerIsMe(): boolean
    get scheduleStates(): ScheduleState[]
}

export const isBookable = (obj: any): boolean => {
    return obj?.iBookable === true
}

const isClassBooking = (bookable: Bookable): boolean => {
    return bookable instanceof Booking && bookable.type === 'class'
}

const isFixedEventReservationBooking = (bookable: Bookable): boolean => {
    return (
        bookable instanceof Booking && bookable.type === 'reservation' && !!bookable.item?.startDate
    )
}

export const isFixedEventReservationItem = (bookable: Bookable): boolean => {
    return bookable instanceof Item && bookable.type === 'reservation' && !!bookable.startDate
}

const isOvernight = (bookable: Bookable): boolean => {
    const start = moment(bookable.date)
    const end = bookable.endDate
        ? moment(bookable.endDate)
        : start.clone().add(bookable.duration || 60, 'minutes')
    return getBookableDuration(bookable) < 1440 && end.dayOfYear() !== start.dayOfYear()
}

export const isMultiDay = (bookable: Bookable): boolean => {
    const start = moment(bookable.date)
    const end = bookable.endDate
        ? moment(bookable.endDate)
        : start.clone().add(bookable.duration || 60, 'minutes')
    return getBookableDuration(bookable) >= 1440 && end.dayOfYear() !== start.dayOfYear()
}

export const getBookableType = (bookable: Bookable, plural = false): string => {
    let type = ''
    if (bookable instanceof Item) type = bookable.type || 'item'
    if (bookable instanceof Booking) type = 'booking'
    if (bookable instanceof Lesson) type = 'class'
    if (bookable instanceof Block) type = 'block'
    if (bookable instanceof PSEvent) type = 'event'
    if (bookable instanceof Task) type = 'task'
    return plural ? (type === 'class' ? 'classes' : `${type}s`) : type
}

export const getBookableDuration = ({ duration, date, endDate }: Bookable): number =>
    duration ?? getDurationInMinutes(date, endDate)

export const getBookableCardSize = (day: moment.Moment, bookable: Bookable) => {
    const duration = isOvernight(bookable)
        ? day.isSame(bookable.date, 'day')
            ? getMinutesToEndOfDay(bookable.date)
            : getMinutesFromStartOfDay(bookable.endDate)
        : getBookableDuration(bookable)

    const height = duration * PIXELS_PER_MINUTE
    if (height >= 84) return 'lg'
    if (height >= 60) return 'md'
    if (height >= 28) return 'sm'
    return 'xs'
}

export const getBookableCardStyle = (
    day: moment.Moment,
    bookable: Bookable,
    offset: number
): React.CSSProperties => {
    const duration = isOvernight(bookable)
        ? day.isSame(bookable.date, 'day')
            ? getMinutesToEndOfDay(bookable.date)
            : getMinutesFromStartOfDay(bookable.endDate)
        : getBookableDuration(bookable)

    return {
        height: Math.max(duration * PIXELS_PER_MINUTE - 3, 0),
        marginTop:
            !isOvernight(bookable) || day.isSame(bookable.date, 'day')
                ? (getMinutesFromStartOfDay(bookable.date) - offset) * PIXELS_PER_MINUTE
                : 0,
        flex: 1,
    }
}

export const getBookableTimeLabel = (
    bookable: Bookable,
    day: moment.Moment,
    restrictToDay = false
): string => {
    const { date, endDate, duration } = bookable

    const start =
        restrictToDay && isOvernight(bookable) && !day.isSame(moment(date), 'day')
            ? moment(date).clone().startOf('day')
            : moment(date)

    const end =
        restrictToDay && isOvernight(bookable) && day.isSame(moment(date), 'day')
            ? moment(endDate).clone().endOf('day')
            : moment(endDate)

    const startLabel = start.format(TIME_FORMAT)
    const endLabel = !!endDate
        ? end.format(TIME_FORMAT)
        : !!date && !!duration && duration > 0
          ? date.clone().add(duration, 'minutes').format(TIME_FORMAT)
          : 'TBD'

    if (isMultiDay(bookable))
        return day.isSame(start, 'day') && day.isSame(end.clone().subtract(1, 'second'), 'day')
            ? 'All Day'
            : day.isSame(start, 'day')
              ? `${startLabel}`
              : day.isSame(end, 'day')
                ? `${endLabel}`
                : 'All Day'

    return `${startLabel} - ${endLabel}`
}

export const getBookableMultiDayLabel = (bookable: Bookable, day: moment.Moment): string => {
    const { date, endDate } = bookable
    const end =
        !!endDate && bookable instanceof Block
            ? endDate.clone().subtract(1, 'second')
            : endDate?.clone()
    const n = !!date ? day.clone().startOf('day').diff(date.clone().startOf('day'), 'days') + 1 : 1
    const N = !!date && !!end ? end.startOf('day').diff(date.clone().startOf('day'), 'days') + 1 : 1
    return isMultiDay(bookable) && N > 1 ? `(day ${n} of ${N})` : ''
}

export const getBookableColor = (bookable: Bookable, payees: Payee[]): Color => {
    const employee =
        !bookable.employee && bookable instanceof Booking ? bookable.job?.person : bookable.employee
    const payee = employee ? payees.find((p) => p.person?.id === employee.id) : null
    const employeeColor = payee?.color ? lookupEmployeeColor(payee.color) : null
    const itemColor = !!bookable.color
        ? lookupBookableColor(bookable.color || 'default')
        : lookupBookableDefaultColor(
              (bookable instanceof Item || bookable instanceof Booking) && !!bookable.type
                  ? bookable.type
                  : 'default'
          )

    return !employeeColor ? itemColor : employeeColor
}

export const getBookableTitles = (bookable: Bookable): [string, string | undefined] => {
    let title, secondaryTitle

    if (bookable instanceof Booking) {
        title = bookable.subaccount?.name ?? bookable.person?.name
        if (bookable.subaccount?.name !== undefined) {
            secondaryTitle = bookable.person?.name
        }
    }
    if (bookable instanceof Task) {
        title = bookable.subaccount?.name ?? bookable.person?.name ?? 'Task'
        if (bookable.subaccount?.name !== undefined) {
            secondaryTitle = bookable.person?.name
        }
    }
    if (bookable instanceof PSEvent) {
        title = bookable.ownerIsMe ? bookable.title : 'Private event'
    }
    if (bookable instanceof Order) {
        title = bookable.subaccount?.name ?? bookable.person?.name
        if (bookable.subaccount?.name !== undefined) {
            secondaryTitle = bookable.person?.name
        }
    }

    return [title || bookable.title || 'No title', secondaryTitle]
}

export const getBookableSubtitle = async (bookable: Bookable): Promise<string> => {
    let subtitle

    if (bookable instanceof Booking) {
        if (!bookable.vendor?.vendorLocation && !!bookable.location) subtitle = bookable.location
        else if (!bookable.confirmedDate) subtitle = bookable.statusString
        else if (bookable.item?.name) subtitle = bookable.item.name
        else subtitle = bookable.statusString
    }
    if (bookable instanceof Lesson) subtitle = await bookable.capacityString()
    if (bookable instanceof Item) subtitle = await bookable.capacityStatus()
    if (bookable instanceof Task) subtitle = bookable.title
    if (bookable instanceof PSEvent && !bookable.ownerIsMe) return ''
    if (bookable instanceof Order)
        subtitle = bookable.item?.name ? bookable.item.name : bookable.shortTitle

    return subtitle || bookable.location || ''
}

type BookableStatus = { color: Color; status: string }

export const getBookableStatus = (bookable: Bookable): BookableStatus => {
    const status = getBookableStatusText(bookable)
    const color = lookupBookableStatusColor(
        status === 'completed' && bookable instanceof Lesson ? 'class' : status
    )
    return { color, status }
}

const getBookableStatusText = (bookable: Bookable) => {
    if (bookable instanceof Booking) {
        if (!!bookable.paidDate || (!!bookable.package && !bookable.amount)) return 'paid'
        if (!bookable.confirmedDate) return 'pending'
        if (bookable.job?.checkinStatus) return bookable.job.checkinStatus.toLowerCase()
        if (bookable.item?.type === 'reservation') return 'reservation'
    }
    if (bookable instanceof Lesson) return !!bookable.completedDate ? 'completed' : 'class'
    if (bookable instanceof Item) return 'reservation'
    if (bookable instanceof Task)
        return !!bookable.completedDate ? 'completed' : isPast(bookable.date) ? 'past due' : 'task'
    if (bookable instanceof PSEvent) return 'personal'
    if (bookable instanceof Block) return 'block'
    if (bookable instanceof Order) return 'pickup'
    return ''
}

export const sortBookablesByStartTime = (a: Bookable, b: Bookable): number => {
    if (isMultiDay(a) && isMultiDay(b)) return a.date?.isBefore(b.date) ? -1 : 1
    if (isMultiDay(a)) return -1
    if (isMultiDay(b)) return 1
    if (a.date?.isSame(b.date)) return Number(a.id) - Number(b.id)
    return a.date?.isBefore(b.date) ? -1 : 1
}

export type OverlappingBookables = {
    start: number
    end: number
    group: Bookable[]
}

export const groupOverlappingSortedBookables = (
    day: moment.Moment,
    bookables: Bookable[]
): OverlappingBookables[] => {
    return bookables.reduce((groups: OverlappingBookables[], bookable: Bookable) => {
        const start =
            !isOvernight(bookable) || day.isSame(bookable.date, 'day')
                ? getMinutesFromStartOfDay(bookable.date)
                : 0
        const end =
            isOvernight(bookable) && day.isSame(bookable.date, 'day')
                ? getMinutesToEndOfDay(bookable.date)
                : getMinutesFromStartOfDay(bookable.endDate)

        let newGroup = {
            start,
            end,
            group: [bookable],
        }

        const isOverlapping = groups.length > 0 && start < groups[groups.length - 1].end

        if (isOverlapping) {
            const lastGroup = groups[groups.length - 1]
            newGroup = {
                start: lastGroup.start,
                end: Math.max(lastGroup.end, end),
                group: [...lastGroup.group, bookable],
            }
        }

        isOverlapping ? groups.splice(-1, 1, newGroup) : groups.push(newGroup)
        return groups
    }, [])
}

export type BookablesByDate = { [date: string]: Bookable[] }

export const getBookablesByDate = (bookables: Bookable[]): BookablesByDate => {
    const filteredBookables = bookables.filter(
        (b) =>
            !isClassBooking(b) &&
            !isFixedEventReservationBooking(b) &&
            (!(b instanceof Item) || isFixedEventReservationItem(b))
    )
    const uniqueBookables = Array.from(new Map(filteredBookables.map((b) => [b.id, b])).values())

    return uniqueBookables.reduce((bookablesByDate: BookablesByDate, bookable: Bookable) => {
        const date = moment(bookable.date)
        const endDate = moment(bookable.endDate)
        const dateKey = date.format(DATE_KEY_FORMAT)

        bookablesByDate[dateKey] =
            dateKey in bookablesByDate ? [...bookablesByDate[dateKey], bookable] : [bookable]

        if (isMultiDay(bookable)) {
            let duration = !!bookable.duration
                ? bookable.duration / 60 / 24
                : endDate.diff(date, 'days')

            if (bookable instanceof Block && endDate.isSame(endDate.clone().startOf('day')))
                duration = endDate.clone().subtract(1, 'second').diff(date, 'days')

            for (let j = 0; j < duration; j++) {
                const nextDate = date
                    .clone()
                    .add(j + 1, 'day')
                    .format(DATE_KEY_FORMAT)

                bookablesByDate[nextDate] =
                    nextDate in bookablesByDate
                        ? [...bookablesByDate[nextDate], bookable]
                        : [bookable]
            }
        }
        else if (isOvernight(bookable)) {
            const nextDate = date.clone().add(1, 'day').format(DATE_KEY_FORMAT)
            bookablesByDate[nextDate] =
                nextDate in bookablesByDate ? [...bookablesByDate[nextDate], bookable] : [bookable]
        }

        return bookablesByDate
    }, {})
}

export const sortedBookablesByDateForRange = (
    bookablesByDate: BookablesByDate,
    start: moment.Moment,
    end: moment.Moment
): BookablesByDate => {
    return Object.fromEntries(
        Object.entries(bookablesByDate)
            .filter(([date]) => moment(date) >= start && moment(date) <= end)
            .map(([date, bookables]) => [date, [...bookables].sort(sortBookablesByStartTime)])
    )
}
